import { API_BASE_URL, API_ORIGIN } from "./api/config";
import {
  httpClient,
  collectFiles,
  buildFormData,
} from "./api/httpClient";
import { resolveResource, buildDynamicPath, buildItemPath } from "./api/resourceMap";

// Si le mapping a un absoluteOverride (ex: /api/geocode/... sans /v1),
// on remplace API_BASE_URL par origin + override.
const baseFor = (cfg) => {
  if (cfg && cfg.absoluteOverride) {
    return `${API_ORIGIN}${cfg.absoluteOverride}`;
  }
  return null;
};

const buildQuery = (params, cfg = {}) => {
  const { pagination, sort, filter } = params;
  const query = { ...(filter || {}) };
  if (pagination) {
    query._page = pagination.page;
    query._perPage = pagination.perPage;
    if (cfg.paginationStyle === "oneIndexed") {
      // Endpoints type EchoController : page 1-indexée + `limit`
      // (PageRequest.of(page-1, limit)). Envoyer page=0 crashe le backend.
      query.page = pagination.page;
      query.limit = pagination.perPage;
    } else {
      query.page = pagination.page - 1;
      query.size = pagination.perPage;
    }
  }
  if (sort?.field) {
    query._sort = sort.field;
    query._order = (sort.order || "asc").toLowerCase();
    query.sort = `${sort.field},${(sort.order || "asc").toLowerCase()}`;
  }
  return Object.entries(query)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) =>
      Array.isArray(v)
        ? v.map((item) => `${encodeURIComponent(k)}=${encodeURIComponent(item)}`).join("&")
        : `${encodeURIComponent(k)}=${encodeURIComponent(v)}`
    )
    .join("&");
};

const parseTotal = (headers, json, fallback) => {
  if (headers) {
    const cr = headers.get && headers.get("content-range");
    if (cr) {
      const match = cr.match(/\/(\d+|\*)$/);
      if (match && match[1] !== "*") return parseInt(match[1], 10);
    }
    const xt = headers.get && headers.get("x-total-count");
    if (xt) return parseInt(xt, 10);
  }
  if (json && typeof json.total === "number") return json.total;
  if (json && typeof json.totalElements === "number") return json.totalElements;
  return fallback;
};

const extractList = (json) => {
  if (Array.isArray(json)) return json;
  if (json?.data && Array.isArray(json.data)) return json.data;
  if (json?.content && Array.isArray(json.content)) return json.content;
  if (json?.items && Array.isArray(json.items)) return json.items;
  return [];
};

// Erreurs de liste : on ne transforme PLUS un 404/405/501 en liste vide
// (cela masquait des endpoints inexistants derrière un « Aucun résultat »).
// L'erreur remonte à react-admin, qui affiche une notification.
const handleListError = (e, url) => {
  if (e?.status === 404 || e?.status === 405 || e?.status === 501) {
    const err = new Error(
      `Endpoint indisponible côté backend (HTTP ${e.status})${url ? ` : ${url.replace(/\?.*$/, "")}` : ""}`
    );
    err.status = e.status;
    throw err;
  }
  throw e;
};

// Un enregistrement react-admin doit avoir un id stable. On n'utilise plus
// l'index de ligne (ids en collision d'une page à l'autre, getOne cassé) :
//  - valeur primitive (ex : liste de chaînes) → la valeur sert d'id ;
//  - objet sans id → `getId(row)` de la ressource si défini, sinon la
//    ligne est ignorée (avec un avertissement console).
const normalizeRows = (rows, resource, cfg) => {
  const out = [];
  let skipped = 0;
  rows.forEach((row) => {
    if (row == null) return;
    if (typeof row !== "object") {
      out.push({ id: row, value: row });
      return;
    }
    if (row.id != null) {
      out.push(row);
      return;
    }
    const derived = typeof cfg?.getId === "function" ? cfg.getId(row) : null;
    if (derived != null) {
      out.push({ ...row, id: derived });
      return;
    }
    skipped += 1;
  });
  if (skipped > 0) {
    // eslint-disable-next-line no-console
    console.warn(`[dataProvider] ${resource} : ${skipped} ligne(s) sans id ignorée(s)`);
  }
  return out;
};

const ensureId = (record, fallbackId) => {
  if (record == null) return { id: fallbackId };
  if (typeof record !== "object") return { id: fallbackId, value: record };
  if (record.id == null) return { ...record, id: fallbackId };
  return record;
};

const unwrapRecord = (json) =>
  json?.data && typeof json.data === "object" && !Array.isArray(json.data) ? json.data : json;

// URL de liste : chemin statique, absoluteOverride, ou chemin dynamique
// construit depuis les filtres (ex : articles/:articleId/comments).
const listUrl = (resource, cfg, params) => {
  let filter = { ...(cfg.listFilter || {}), ...(params.filter || {}) };
  if (typeof cfg.transformFilter === "function") filter = cfg.transformFilter(filter);
  const { path, rest, used } = buildDynamicPath(resource, cfg, filter);
  const qs = buildQuery({ ...params, filter: rest }, cfg);
  const overrideBase = baseFor(cfg);
  const base = overrideBase || `${API_BASE_URL}/${path}`;
  return { url: qs ? `${base}?${qs}` : base, used };
};

// Filtre appliqué côté BO (ex : masquer les comptes supprimés) : sa clé est
// retirée des paramètres envoyés au backend.
const splitClientFilter = (cfg, params) => {
  if (!cfg.clientFilter) return { params, exclude: null };
  const { key, exclude } = cfg.clientFilter;
  const filter = { ...(params.filter || {}) };
  const show = filter[key];
  delete filter[key];
  return { params: { ...params, filter }, exclude: show === true || show === "true" ? null : exclude };
};

const fetchList = async (resource, cfg, rawParams) => {
  const { params, exclude } = splitClientFilter(cfg, rawParams);
  let built;
  try {
    built = listUrl(resource, cfg, params);
  } catch (e) {
    // Filtre parent obligatoire absent : aucun appel backend (rien à
    // masquer), l'écran affiche la consigne de saisie du filtre.
    if (e?.code === "MISSING_PARENT") return { data: [], total: 0 };
    throw e;
  }
  const { url, used } = built;
  try {
    const { json, headers } = await httpClient(url);
    // transformList : mappe une réponse non-liste (DTO agrégé…) en lignes.
    const rows = typeof cfg.transformList === "function"
      ? cfg.transformList(json, used)
      : extractList(json);
    // Les paramètres du chemin parent (proId, articleId…) sont recopiés sur
    // chaque ligne quand elle ne les porte pas (utile à itemPath).
    const all = normalizeRows(rows || [], resource, cfg).map((row) =>
      Object.keys(used).length ? { ...used, ...row } : row
    );
    const data = exclude ? all.filter((r) => !exclude(r)) : all;
    if (cfg.clientSide) return paginateClientSide(data, params, cfg.clientSide);
    const total = parseTotal(headers, json, all.length) - (all.length - data.length);
    return { data, total };
  } catch (e) {
    return handleListError(e, url);
  }
};

// URL d'écriture (PUT/PATCH) : `updatePath(id, data)` si la ressource a un
// endpoint de mise à jour différent de sa lecture (ex : /users/pro/{id}).
const updateUrl = (resource, cfg, id, data) =>
  typeof cfg.updatePath === "function"
    ? `${API_BASE_URL}/${cfg.updatePath(id, data || {})}`
    : itemUrl(resource, cfg, id, data);

// URL de suppression : `deletePath(id, data)` si différente de la lecture.
const deleteUrl = (resource, cfg, id, data) =>
  typeof cfg.deletePath === "function"
    ? `${API_BASE_URL}/${cfg.deletePath(id, data || {})}`
    : itemUrl(resource, cfg, id, data);

// Endpoints qui renvoient la collection complète sans pagination ni
// recherche (ex : GET /users/specialities) : recherche `q`, tri et
// pagination faits ici, total = nombre de lignes filtrées.
const paginateClientSide = (rows, params, opts = {}) => {
  let out = rows;
  const q = params.filter?.q;
  if (q) {
    const needle = String(q).toLowerCase();
    const fields = opts.searchFields || [];
    out = out.filter((r) =>
      fields.some((f) => String(r?.[f] ?? "").toLowerCase().includes(needle))
    );
  }
  const { field, order } = params.sort || {};
  if (field) {
    const dir = String(order).toUpperCase() === "DESC" ? -1 : 1;
    out = [...out].sort((a, b) => {
      const va = a?.[field];
      const vb = b?.[field];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      return String(va).localeCompare(String(vb), "fr", { numeric: true }) * dir;
    });
  }
  const total = out.length;
  if (params.pagination) {
    const { page, perPage } = params.pagination;
    out = out.slice((page - 1) * perPage, page * perPage);
  }
  return { data: out, total };
};

const itemUrl = (resource, cfg, id, data) => {
  const path = buildItemPath(resource, cfg, id, data);
  return `${API_BASE_URL}/${path}`;
};

export const dataProvider = {
  getList: async (resource, params) => {
    const cfg = resolveResource(resource);
    return fetchList(resource, cfg, params);
  },

  getOne: async (resource, params) => {
    const cfg = resolveResource(resource);
    if (cfg.getOneFromList) {
      // Pas d'endpoint GET /{id} : on relit la collection (grande page pour
      // les listes paginées côté back — sinon seule la 1re page de 50
      // serait parcourue ; les listes `clientSide` sont relues entières).
      const { data } = await fetchList(resource, cfg, {
        filter: {},
        pagination: cfg.clientSide
          ? undefined
          : { page: 1, perPage: cfg.getOneFromListPerPage || 1000 },
      });
      const found = data.find((r) => String(r.id) === String(params.id));
      if (!found) {
        const err = new Error(`« ${resource} » : enregistrement ${params.id} introuvable`);
        err.status = 404;
        throw err;
      }
      return { data: found };
    }
    const { json } = await httpClient(itemUrl(resource, cfg, params.id, params.meta));
    const record = unwrapRecord(json);
    return { data: ensureId(record, params.id) };
  },

  // Pas de ?id=a&id=b (aucun endpoint backend ne le supporte : on recevait
  // la première page de la collection, mappée sur de mauvais ids) : un
  // getOne par id.
  getMany: async (resource, params) => {
    const data = await Promise.all(
      params.ids.map((id) =>
        dataProvider
          .getOne(resource, { id, meta: params.meta })
          .then(({ data: rec }) => rec)
          .catch(() => ({ id }))
      )
    );
    return { data };
  },

  getManyReference: async (resource, params) => {
    const cfg = resolveResource(resource);
    return fetchList(resource, cfg, {
      ...params,
      filter: { ...(params.filter || {}), [params.target]: params.id },
    });
  },

  create: async (resource, params) => {
    const cfg = resolveResource(resource);
    const path = typeof cfg.createPath === "function"
      ? cfg.createPath(params.data || {})
      : cfg.dynamicPath
        ? buildDynamicPath(resource, cfg, params.data || {}).path
        : cfg.path;
    const files = collectFiles(params.data);
    const body =
      files.length > 0
        ? buildFormData(params.data)
        : JSON.stringify(params.data);
    const { json } = await httpClient(`${API_BASE_URL}/${path}`, {
      method: "POST",
      body,
    });
    const created = unwrapRecord(json);
    const id =
      (created && typeof created === "object" && created.id) ||
      (typeof created === "string" || typeof created === "number"
        ? created
        : params.data.id);
    return { data: { ...params.data, ...(created && typeof created === "object" ? created : {}), id } };
  },

  update: async (resource, params) => {
    const cfg = resolveResource(resource);
    const files = collectFiles(params.data);
    const body =
      files.length > 0
        ? buildFormData(params.data)
        : JSON.stringify(params.data);
    const { json } = await httpClient(
      updateUrl(resource, cfg, params.id, { ...(params.previousData || {}), ...(params.data || {}) }),
      { method: cfg.updateMethod || "PUT", body }
    );
    const updated = unwrapRecord(json) || {};
    return { data: { ...params.data, ...(typeof updated === "object" ? updated : {}), id: params.id } };
  },

  updateMany: async (resource, params) => {
    const cfg = resolveResource(resource);
    const results = await Promise.allSettled(
      params.ids.map((id) =>
        httpClient(updateUrl(resource, cfg, id, params.data), {
          method: cfg.updateMethod || "PUT",
          body: JSON.stringify(params.data),
        })
      )
    );
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      const err = new Error(
        `${failed.length}/${params.ids.length} mise(s) à jour refusée(s) : ${failed[0].reason?.message || ""}`
      );
      err.status = failed[0].reason?.status;
      throw err;
    }
    return { data: params.ids };
  },

  delete: async (resource, params) => {
    const cfg = resolveResource(resource);
    await httpClient(deleteUrl(resource, cfg, params.id, params.previousData), {
      method: "DELETE",
    });
    return { data: { id: params.id } };
  },

  deleteMany: async (resource, params) => {
    const cfg = resolveResource(resource);
    // On ne masque plus les échecs : avant, une suppression refusée par le
    // back était affichée comme réussie puis l'élément réapparaissait.
    const results = await Promise.allSettled(
      params.ids.map((id) =>
        // deletePath dépend de l'enregistrement (ex : auteur d'un article) :
        // react-admin ne fournit que les ids → on relit chaque enregistrement.
        (typeof cfg.deletePath === "function"
          ? dataProvider.getOne(resource, { id }).then(({ data }) => data)
          : Promise.resolve(undefined)
        ).then((rec) => httpClient(deleteUrl(resource, cfg, id, rec), { method: "DELETE" }))
      )
    );
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      const err = new Error(
        failed.length === params.ids.length
          ? failed[0].reason?.message || "Suppression impossible"
          : `${failed.length}/${params.ids.length} suppression(s) refusée(s) : ${failed[0].reason?.message || ""}`
      );
      err.status = failed[0].reason?.status;
      throw err;
    }
    return { data: params.ids };
  },
};

export default dataProvider;
